import {
  aggregateFunctionsForField,
  aggregators,
  columnInfo,
} from "@vzbl/integrations";
import * as React from "react";
import { FormulaIcon } from "src/components/FormulaIcon";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { Aggregation, Comparator, Condition, Field } from "src/search/types";
import { useEntitySelectors } from "src/search/useEntitySelectors";
import { asKey } from "src/utils/fields";
import { attributeToTitle } from "src/utils/strings";
import { Clause } from "./Clause";
import { MeasureByInputClause, UseQuestionInputClauses } from "./definitions";
import { theme, Space, Select, Button } from "antd";
import { usePopupContext } from "src/hooks/usePopup";
import { FieldSelector } from "./FieldSelector";
import { useQuery } from "react-query";
import { getFacetOptions } from "pages/api-clients/graph-search";
import { useApplicationContext } from "src/hooks/useApplication";
import { DateRangeSelect } from "src/components/DateRangeSelect";
import { NumbersInput } from "./NumbersInput";
import { durationToRange } from "src/utils/date";
import { FieldTooltip } from "./ClauseTooltips";
import styles from "./question-input.module.css";
import { DeleteOutlined } from "@ant-design/icons";
import { isValidElement } from "react";

type AggInputValue = Aggregation & {
  filter?: {
    field?: Field;
    operator?: string;
    values?: any[];
  };
};

// Panel component that handles its own state independently
function MeasureByPanel({
  initialValue,
  value,
  onApply,
  onCancel,
}: {
  initialValue: Partial<AggInputValue>;
  value: any;
  onApply: (data: AggInputValue) => void;
  onCancel: () => void;
}) {
  const { token } = theme.useToken();
  const integrationConfig = useIntegrationConfig();
  const orgId = useApplicationContext().org_id!;

  // Local state for the panel
  const [data, setData] = React.useState<Partial<AggInputValue>>(initialValue);

  // Reset data when panel opens with new initial value
  React.useEffect(() => {
    setData(initialValue);
  }, [initialValue]);

  // Get available functions based on selected field
  const availableFunctions = React.useMemo(() => {
    if (!data.target || !data.attribute) return [];
    return aggregateFunctionsForField(
      {
        target: data.target,
        attribute: data.attribute,
      },
      integrationConfig.data as any
    );
  }, [data.target, data.attribute, integrationConfig.data]);

  // Format function options
  const functionOptions = React.useMemo(
    () =>
      aggregators
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .map((func) => ({
          value: func,
          label: (
            <div style={{ display: "flex", gap: 4 }}>
              <span>{func}</span>
              <span style={{ opacity: 0.5 }}>
                ({data.attribute || "field"})
              </span>
            </div>
          ),
          searchText: `${func} ${data.attribute || ""}`.toLowerCase(),
          disabled: !availableFunctions.includes(func),
        })),
    [availableFunctions, data.attribute]
  );

  // Handle field change
  const handleFieldChange = (field: Field | undefined) => {
    setData((current) => ({
      ...current,
      target: field?.target,
      attribute: field?.attribute,
      reducer: undefined, // Reset reducer when field changes
    }));
  };

  // Get column metadata for the filter field
  const columnMeta = React.useMemo(
    () =>
      columnInfo(
        asKey(data.filter?.field || ({} as Field)),
        integrationConfig.data as any
      ),
    [data.filter?.field, integrationConfig.data]
  );

  // Get available operators based on field type with descriptions
  const operators = React.useMemo(() => {
    if (!data.filter?.field) return [];

    const ops = [
      {
        value: "IN",
        label: "IS IN",
        description: "Select one or more values to include",
      },
      {
        value: "NOT IN",
        label: "NOT IN",
        description: "Select one or more values to exclude",
      },
    ];

    if (columnMeta.type === "number") {
      ops.push({
        value: "WITHIN",
        label: "BETWEEN",
        description: `Enter a range of numbers${
          columnMeta.unit ? ` (in ${columnMeta.unit})` : ""
        }`,
      });
    } else if (columnMeta.type === "datetime") {
      ops.push({
        value: "WITHIN",
        label: "BETWEEN",
        description: "Select a date range or duration",
      });
    }

    return ops;
  }, [columnMeta.type, columnMeta.unit, data.filter?.field]);

  // Enhanced search function for values
  const searchValues = React.useCallback((input: string, option: any) => {
    if (!input.trim()) return true;
    if (!option) return false;

    const searchableText = (
      option.label === "n/a" ? "__VZBL::null__" : option.label
    ).toLowerCase();
    const searchTerms = input.toLowerCase().split(/\s+/);

    return searchTerms.every((term) => searchableText.includes(term));
  }, []);

  // Query for field values
  const query = useQuery(
    ["search/inputs/conditionForm", data.filter?.field],
    async () => {
      if (!data.filter?.field?.target || !data.filter?.field?.attribute) {
        return { data: [] };
      }

      return getFacetOptions(
        { select: value.select, search: value.search },
        {
          target: data.filter.field.target,
          attribute: data.filter.field.attribute,
        },
        orgId
      );
    },
    {
      enabled: Boolean(
        data.filter?.field?.target && data.filter?.field?.attribute
      ),
    }
  );

  const { aggregations: relevantFields } = useEntitySelectors({ state: value });

  // Format value for display
  const formatValue = (value: any) => {
    if (value === "__VZBL::null__") return "N/A";
    if (typeof value === "string") {
      // Convert camelCase or snake_case to Title Case
      return value
        .replace(/_/g, " ")
        .split(/(?=[A-Z])/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    return value;
  };

  // Render value selector based on field type and operator
  const renderValueSelector = () => {
    if (!data.filter?.field || !data.filter?.operator) return null;
    const filter = data.filter; // Create a local variable to help TypeScript understand the type

    if (columnMeta.type === "number" && filter.operator === "WITHIN") {
      return (
        <NumbersInput
          unit={columnMeta.unit}
          values={filter.values || []}
          onChange={({ comparison }) => {
            const newValues = Object.values(comparison)[0];
            setData((current) => ({
              ...current,
              filter: { ...filter, values: newValues },
            }));
          }}
          condition={
            {
              ...filter.field,
              comparison: {
                [filter.operator]: filter.values,
              },
            } as Condition
          }
        />
      );
    }

    if (columnMeta.type === "datetime" && filter.operator === "WITHIN") {
      return (
        <DateRangeSelect
          startDate={filter.values?.[0]}
          endDate={filter.values?.[1]}
          onChangeRange={(vals) =>
            setData((current) => ({
              ...current,
              filter: { ...filter, values: vals },
            }))
          }
          onChangeDuration={(duration) => {
            const { startDate, endDate } = durationToRange(duration);
            setData((current) => ({
              ...current,
              filter: { ...filter, values: [startDate, endDate] },
            }));
          }}
        />
      );
    }

    return (
      <Select
        mode="multiple"
        style={{ width: "100%" }}
        showSearch
        maxTagCount="responsive"
        maxTagTextLength={20}
        listHeight={256}
        virtual={false}
        placeholder="Select values"
        value={filter.values}
        onChange={(vals) => {
          setData((current) => ({
            ...current,
            filter: { ...filter, values: vals },
          }));
        }}
        options={
          query.data?.data.map(({ value, label }) => ({
            value,
            label: formatValue(label),
          })) || []
        }
        filterOption={searchValues}
        optionFilterProp="children"
        maxTagPlaceholder={(omittedValues) =>
          `+ ${omittedValues.length} more...`
        }
        dropdownRender={(menu) => (
          <div>
            {menu}
            {filter.values && filter.values.length > 0 && (
              <div
                style={{
                  padding: "8px 12px",
                  borderTop: `1px solid ${token.colorBorderSecondary}`,
                  fontSize: "0.85em",
                  color: token.colorTextDescription,
                }}
              >
                {filter.values.length} selected
              </div>
            )}
          </div>
        )}
      />
    );
  };

  return (
    <div
      className={styles.clausePanel}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "350px",
      }}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Field Selection */}
        <div>
          <FieldTooltip type="measure">
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Choose a Metric
            </div>
          </FieldTooltip>
          <FieldSelector
            value={
              data.target && data.attribute
                ? ({ target: data.target, attribute: data.attribute } as Field)
                : undefined
            }
            onChange={handleFieldChange}
            placeholder="Search for a field to measure..."
            relevantFields={relevantFields}
          />
        </div>

        {/* Aggregation Selection */}
        {data.target && data.attribute && (
          <div>
            <FieldTooltip type="aggregation">
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: token.colorTextSecondary,
                  marginBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Select Aggregation
              </div>
            </FieldTooltip>
            <Select
              style={{ width: "100%" }}
              placeholder="Choose how to aggregate..."
              value={data.reducer}
              onChange={(reducer) =>
                setData((current) => ({ ...current, reducer }))
              }
              options={functionOptions}
              listHeight={256}
              virtual={false}
              showSearch
              filterOption={(input, option) => {
                if (!option?.searchText) return false;
                return option.searchText.includes(input.toLowerCase());
              }}
              optionFilterProp="searchText"
              dropdownStyle={{
                maxHeight: 256,
              }}
              onPopupScroll={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              open={data.target && data.attribute ? undefined : false}
            />
          </div>
        )}

        {/* Optional Filter */}
        {data.target && data.attribute && (
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 4,
                marginTop: 12,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Apply Filter (Optional)
              <span
                style={{
                  fontSize: 11,
                  padding: "2px 6px",
                  background: token.colorPrimaryBg,
                  color: token.colorPrimary,
                  borderRadius: 4,
                  textTransform: "none",
                  letterSpacing: "normal",
                }}
              >
                Measure Filter
              </span>
            </div>

            <div
              style={{
                padding: "16px",
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.03)",
              }}
            >
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                {/* Filter Field Selection */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <FieldTooltip type="measureFilterField">
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: token.colorTextSecondary,
                          marginBottom: 4,
                        }}
                      >
                        Select a Field to Filter By
                      </div>
                    </FieldTooltip>
                    <FieldSelector
                      value={data.filter?.field}
                      onChange={(field) =>
                        setData((current) => ({
                          ...current,
                          filter: {
                            field,
                            operator: undefined,
                            values: undefined,
                          },
                        }))
                      }
                      placeholder="Filter the measure..."
                      relevantFields={relevantFields}
                    />
                  </div>
                  {data.filter && (
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        setData((current) => ({
                          ...current,
                          filter: undefined,
                        }))
                      }
                      style={{
                        marginLeft: 8,
                        color: token.colorError,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = token.colorErrorHover;
                        e.currentTarget.style.backgroundColor =
                          token.colorErrorBg;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = token.colorError;
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    />
                  )}
                </div>

                {/* Operator Selection */}
                {data.filter?.field && (
                  <div>
                    <FieldTooltip type="measureFilterOperator">
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: token.colorTextSecondary,
                          marginBottom: 4,
                        }}
                      >
                        Select Operator
                      </div>
                    </FieldTooltip>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Choose how to filter..."
                      value={data.filter.operator}
                      onChange={(op) =>
                        setData((current) => ({
                          ...current,
                          filter: {
                            ...current.filter!,
                            operator: op,
                            values: undefined,
                          },
                        }))
                      }
                      options={operators}
                    />
                  </div>
                )}

                {/* Value Selection */}
                {data.filter?.field && data.filter?.operator && (
                  <div>
                    <FieldTooltip type="measureFilterValue">
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: token.colorTextSecondary,
                          marginBottom: 4,
                        }}
                      >
                        Enter Values
                      </div>
                    </FieldTooltip>
                    {renderValueSelector()}
                  </div>
                )}
              </Space>
            </div>
          </div>
        )}

        <div>
          {/* Summary of current measure and filter */}
          <div
            className={styles.clauseSummary}
            style={{ background: token.colorBgContainerDisabled }}
          >
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: token.colorText }}>Measuring: </span>
              {data.reducer ? (
                <span>
                  {data.reducer.toLowerCase()}
                  <span style={{ opacity: 0.7 }}> of </span>
                  <strong>{attributeToTitle(data.attribute)}</strong>
                  <span style={{ opacity: 0.7 }}> from </span>
                  <strong>{attributeToTitle(data.target)}</strong>
                </span>
              ) : (
                <span style={{ opacity: 0.7 }}>
                  Select a measure and aggregation method
                </span>
              )}
            </div>

            {data.filter?.field && (
              <div>
                <span style={{ color: token.colorText }}>Filtered to: </span>
                {data.filter.operator && data.filter.values ? (
                  <span>
                    <strong>
                      {attributeToTitle(data.filter.field.attribute)}
                    </strong>
                    <span style={{ opacity: 0.7 }}> is </span>
                    {data.filter.operator.toLowerCase()}
                    <span style={{ opacity: 0.7 }}> </span>
                    <strong>
                      {Array.isArray(data.filter.values)
                        ? data.filter.values
                            .map((v) => formatValue(v))
                            .join(", ")
                        : formatValue(data.filter.values)}
                    </strong>
                  </span>
                ) : (
                  <span style={{ opacity: 0.7 }}>
                    Configure filter conditions
                  </span>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              paddingTop: 16,
              marginTop: 8,
            }}
          >
            <div
              style={{
                fontSize: "0.85em",
                color: token.colorTextDescription,
              }}
            >
              {(() => {
                if (!data.target) return "Select a field to begin";
                if (!data.reducer) return "Choose function to apply";
                return "Ready to apply measure";
              })()}
            </div>
            <Space>
              <button
                onClick={onCancel}
                style={{
                  background: "none",
                  border: "none",
                  padding: "5px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  color: "rgba(0, 0, 0, 0.65)",
                  fontSize: 14,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  fontSize: 14,
                  backgroundColor: "#1677ff",
                  color: "white",
                  border: "none",
                  padding: "5px 16px",
                  borderRadius: 6,
                  cursor:
                    data.target && data.attribute && data.reducer
                      ? "pointer"
                      : "not-allowed",
                  opacity:
                    data.target && data.attribute && data.reducer ? 1 : 0.5,
                  transition: "all 0.2s",
                  boxShadow: "0 2px 0 rgba(5, 145, 255, 0.1)",
                }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = "#4096ff";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = "#1677ff";
                  }
                }}
                disabled={!data.target || !data.attribute || !data.reducer}
                onClick={() => {
                  if (data.target && data.attribute && data.reducer) {
                    const agg = { ...data } as AggInputValue;
                    const filter = data.filter;
                    delete agg.filter;

                    // Explicitly set filters based on whether we have a filter or not
                    agg.filters =
                      filter?.field &&
                      filter.operator &&
                      filter.values &&
                      filter.values.length > 0
                        ? [
                            {
                              ...filter.field,
                              comparison: {
                                [filter.operator]: filter.values,
                              } as Comparator,
                            } as Condition,
                          ]
                        : [];

                    onApply(agg);
                  }
                }}
              >
                Apply
              </button>
            </Space>
          </div>
        </div>
      </Space>
    </div>
  );
}

// Pill component that displays the current value
function MeasureByPill({ data }: { data: Partial<AggInputValue> }) {
  let displayValue;
  displayValue = isValidElement(data.reducer) ? (
    data.reducer
  ) : (
    <span>{data.reducer}</span>
  );

  return (
    <span>
      {displayValue}
      {data.target && data.attribute && (
        <>
          <span style={{ opacity: 0.5 }}>{"("}</span>
          <span style={{ opacity: 0.65 }}>{data.target}.</span>
          <span>{data.attribute}</span>
          <span style={{ opacity: 0.5 }}>{")"}</span>
          {data.filters?.length &&
            data.filters.length > 0 &&
            data.filters[0].comparison && (
              <>
                <span style={{ opacity: 0.5 }}>{"["}</span>
                {Object.entries(data.filters[0].comparison).map(
                  ([op, vals]) => (
                    <span key={op}>
                      {Array.isArray(vals) ? vals.join(", ") : vals}
                    </span>
                  )
                )}
                <span style={{ opacity: 0.5 }}>{"]"}</span>
              </>
            )}
        </>
      )}
    </span>
  );
}

export function MeasureByClause({
  clause,
  value,
  updateClause,
  removeClause,
  sameAsLast,
}: {
  clause: MeasureByInputClause;
  value: any;
  updateClause: (clause: any) => void;
  removeClause: (clause: any) => void;
  sameAsLast?: boolean;
}) {
  const popup = usePopupContext();
  const data = React.useMemo(
    () => ({
      ...clause.value,
      filter: clause.value?.filters?.length
        ? {
            field: {
              target: clause.value.filters[0].target,
              attribute: clause.value.filters[0].attribute,
            } as Field,
            operator: Object.keys(clause.value.filters[0].comparison || {})[0],
            values: Object.values(clause.value.filters[0].comparison || {})[0],
          }
        : undefined,
    }),
    [clause.value]
  );

  const handleApply = React.useCallback(
    (newData: AggInputValue) => {
      updateClause({
        ...clause,
        value: newData,
      });
      popup.close();
    },
    [clause, updateClause, popup]
  );

  const renderMenu = React.useCallback(
    () => (
      <MeasureByPanel
        initialValue={data}
        onApply={handleApply}
        onCancel={() => popup.close()}
        value={value}
      />
    ),
    [data, handleApply, popup, value]
  );

  const icon = sameAsLast ? undefined : (
    <FormulaIcon size={10} style={{ opacity: 0.7 }} />
  );

  return (
    <Clause
      onRemove={() => removeClause(clause)}
      onMenuClose={() => popup.close()}
      renderMenu={renderMenu}
      clause={clause}
      title={sameAsLast ? "" : "Measures"}
      icon={icon}
    >
      <MeasureByPill data={data} />
    </Clause>
  );
}
